/**
 * @fileoverview Functions for extracting interactions from ProLIF LID html and
 * associating them with BMaps interactions.
 */
import _ from 'lodash';

import { UserActions } from 'BMapsCmds';
import { filterAromaticRings, Ring } from 'BMapsModel';
import { annotatePiPi, DefaultPiPiParams } from 'BMapsSrc/interactions/pipi';
import { formResidueSpec, parseResidueSpec } from 'BMapsSrc/util/mol_info_utils';

/**
 * @fileoverview This file contains functions and classes for converting interactions defined by
 * the ProLif LID to BMaps interactions.
 *
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').Residue} Residue
 * @typedef {{ activeCompound: Compound, residues: Residue[] }} BindingSiteContext}
 * @typedef {import('BMapsSrc/interactions/pipi').PiPiStack} PiPiStack
 * @typedef {import('BMapsSrc/interactions/pipi').PiPiParams} PiPiParams
 */

/**
 * Get ProLif interactions for a given compound and binding site residues.
 * @param {BindingSiteContext} bindingSiteContext
 * @param {any} params
 * @returns {{ [interactionType: string]: ProLifInteraction[] }}
 */

export async function getProLifInteractions(bindingSiteContext, params={}) {
    const { activeCompound: compound } = bindingSiteContext;
    if (!compound) return [];

    const { html, error, pdbObj } = await UserActions.FetchProLifData({ compound });
    if (error) {
        console.error('Error fetching ProLIF data for pi-pi:', error);
        return [];
    }
    const interactions = getProLifInteractionsFromHtml(html, bindingSiteContext, params, pdbObj);
    return interactions;
}

/**
 * Parse ProLif HTML and extract interactions.
 * @param {string} html
 * @param {BindingSiteContext} bindingSiteContext
 * @param {{ [interactionType: string]: object }} params
 * @param {import('BMapsUtil/pdb').PdbData} pdbObj Source PDB data, used for original chain lookup
 * @returns {{ [interactionType: string]: ProLifInteraction[] }}
 */
export function getProLifInteractionsFromHtml(html, bindingSiteContext, params={}, pdbObj) {
    try {
        const lidInteractions = ProLifInteraction.extractFromLidHtml(html);
        const interactions = {};
        for (const [type, byType] of Object.entries(lidInteractions)) {
            const assocInt = associateInteractions(
                type, byType, bindingSiteContext, params[type], pdbObj
            );
            if (!interactions[type]) interactions[type] = [];
            interactions[type].push(...assocInt);
        }

        console.log(`ProLif interactions: ${Object.values(interactions).join('; ')}`);
        return interactions;
    } catch (ex) {
        console.error('Exception processing ProLIF data for pi-pi:', ex);
        return {};
    }
}

/**
 * For a binding site context (compound + residues), fetch ProLif pi-pi interactions
 * and return corresponding BMaps Pi-Pi data structures.
 * @param {BindingSiteContext} bindingSiteContext
 * @param {PiPiParams} params
 * @returns {PiPiStack[]}
 */
export async function getProLifPipiStacks(bindingSiteContext, params=DefaultPiPiParams) {
    const type = ProLifInteraction.Types.PiStacking;
    const interactions = await getProLifInteractions(bindingSiteContext, { [type]: params });
    return (interactions[type] || [])
        .map((i) => i.bmapsInteraction)
        .filter(Boolean);
}

/**
 * Class to represent an interaction returned by ProLif.
 */
export class ProLifInteraction {
    static get Types() {
        return {
            PiStacking: 'PiStacking',
        };
    }

    constructor(edge) {
        this.edge = edge;
        this.interactionType = edge.interaction_type;
        this.residue = ProLifInteraction.lidSpecToBMapsSpec(edge.to) ?? '';
        this.distance = ProLifInteraction.distanceFromTitle(edge.title, this.interactionType) ?? -1;
    }

    toString() {
        return `${this.interactionType}: ${this.residue} (${this.distance.toFixed(2)}\u00c5)`;
    }

    /**
     *
     * @param {string} lidHtml
     * @returns {{ [interactionType: string]: ProLifInteraction[] }}
     */
    static extractFromLidHtml(lidHtml) {
        const pattern = /^\s*edges = (\[.*);$/m; // look for `        edges = [...];`
        const edgesData = lidHtml.match(pattern);
        const edges = JSON.parse(edgesData[1]).filter((edge) => edge.interaction_type);
        const ret = {};
        for (const edge of edges) {
            const newInter = new ProLifInteraction(edge);
            const type = newInter.interactionType;
            if (!ret[type]) ret[type] = [];
            ret[type].push(newInter);
        }
        return ret;
    }

    static lidSpecToBMapsSpec(spec) {
        const pattern = /(?<resn>[A-Z]+)(?<seq>\d+)\.(?<chain>[A-Z]+)/;
        const fields = spec.match(pattern)?.groups;
        return fields ? `${fields.resn.toUpperCase()}.${fields.seq}:${fields.chain}` : null;
    }

    static distanceFromTitle(title, interactionType) {
        const distMatch = title.match(`^${interactionType}: ([\\d.]+)\u00c5$`);
        return distMatch ? Number(distMatch[1]) : null;
    }
}

/**
 * Map ProLif interactions to pairs with corresponding BMaps interactions.
 * @param {string} type
 * @param {ProLifInteraction[]} lidInteractions
 * @param {BindingSiteContext} bindingSiteContext
 * @param {any} params
 * @param {import('BMapsUtil/pdb').PdbData} pdbObj Source PDB data, used for original chain lookup
 * @returns {ProLifInteractionAssociation[]}
 */
function associateInteractions(type, lidInteractions, bindingSiteContext, params={}, pdbObj) {
    const transforms = {
        [ProLifInteraction.Types.PiStacking]: associatePiPi,
        default: (lis) => lis.map((li) => new ProLifInteractionAssociation(li)),
    };
    const transform = transforms[type] || transforms.default;
    return transform(lidInteractions, bindingSiteContext, params[type], pdbObj);
}

/**
* Match ProLif pi-pi interactions with BMaps ring-ring interactions.
* @param {ProLifInteraction[]} lidPiPis
* @param {BindingSiteContext} bindingSiteContext
* @param {PiPiParams} params
* @param {import('BMapsUtil/pdb').PdbData} pdbObj Source PDB data, used for original chain lookup
* @returns {ProLifInteractionAssociation[]}
 */
function associatePiPi(lidPiPis, bindingSiteContext, params, pdbObj) {
    const { activeCompound, residues } = bindingSiteContext;
    const originalRings = Ring.ringsForAtomGroup(activeCompound);
    const originalAromaticRings = filterAromaticRings(originalRings);
    const nonAromaticRings = originalRings.filter((r) => !originalAromaticRings.includes(r));
    const preferredAromaticRings = Ring.combineAromaticRings(originalRings);
    const cmpdRings = [...preferredAromaticRings, ...nonAromaticRings];

    const countMerged = (s) => (
        (s.ring1.isMergedRing() ? 1 : 0) + (s.ring2.isMergedRing() ? 1 : 0)
    );

    function sortStackEntries(stack1, stack2) {
        return stack1.delta - stack2.delta;
    }

    function getOriginalChainSpec(resSpecIn) {
        // This is to handle the case where BMaps has a multi-character chain ID,
        // but it was converted to a single character chain for the pdb data passed to the LID.
        // We now have a LID residue spec with a possibly converted chain ID, and trying to find
        // the BMaps residue (with possibly multi-character chain ID) that matches it.
        // Convert the LID spec to have the original chain ID, so we can lookup the BMaps residue.
        let resSpec = resSpecIn;

        if (pdbObj) {
            const parsed = parseResidueSpec(resSpec);
            const originalChain = pdbObj.lookupOriginalChain(parsed.chain);
            if (originalChain !== parsed.chain) {
                resSpec = formResidueSpec(parsed.resn, parsed.resi, originalChain);
            }
        }
        return resSpec;
    }

    const lidToStacksMap = new Map();
    for (const lidPiPi of lidPiPis) {
        const lidResSpec = getOriginalChainSpec(lidPiPi.residue);
        const residue = residues.find(
            // ProLIF residues don't have altloc
            (res) => formResidueSpec(res.resn, res.resi, res.chain) === lidResSpec
        );
        if (!residue) continue;
        const resRings = Ring.combineAromaticRings(Ring.ringsForAtomGroup(residue));
        const stackEntries = cmpdRings.flatMap((cmpdRing) => (
            resRings.map((resRing) => {
                const stack = annotatePiPi(cmpdRing, resRing, params);
                return {
                    stack,
                    distance: stack.distance,
                    delta: Math.abs(stack.distance - lidPiPi.distance),
                    toString() {
                        return `${stack.ring1} - ${stack.ring2} (${this.distance.toFixed(2)}/${this.delta.toFixed(2)}), merged count: ${countMerged(stack)})`;
                    },
                };
            })
        ));
        stackEntries.sort(sortStackEntries);
        const [pipiStacks, otherStacks] = _.partition(stackEntries, (e) => e.stack.pipiType);
        lidToStacksMap.set(lidPiPi, { residue, pipiStacks, otherStacks });
    }

    // This is written as if we'll need to attempt to reconfigure the assignments if not unique.
    const assignments = new Map();
    for (const [lidPiPi, { pipiStacks, otherStacks }] of lidToStacksMap) {
        const preferredEntry = pipiStacks[0] || otherStacks[0];
        assignments.set(lidPiPi, preferredEntry);
    }

    const getResults = () => {
        const ret = { results: [], skipped: [] };
        const used = new Set();
        for (const [lidPiPi, entry] of assignments) {
            if (!entry) {
                console.warn(`No pi-pi associations found for ${lidPiPi}`);
                ret.skipped.push(lidPiPi);
                continue;
            }
            if (used.has(entry)) {
                ret.skipped.push(lidPiPi);
                continue;
            }
            ret.results.push(new ProLifInteractionAssociation(lidPiPi, entry.stack));
            used.add(entry);
        }
        return ret;
    };

    const { results, skipped } = getResults();

    if (results.length !== lidPiPis.length) {
        console.warn(`Ambiguous pi-pi interactions detected from ProLif. Skipping:\n${skipped.join('\n')}`);
    }

    return results;
}

/**
 * Class to connect the ProLif representation of an interaction with the BMaps representation.
 */
class ProLifInteractionAssociation {
    constructor(lidInteraction, bmapsInteraction) {
        this.type = lidInteraction.interactionType;
        this.lidInteraction = lidInteraction;
        this.bmapsInteraction = bmapsInteraction;
    }

    toString() {
        return `${this.lidInteraction} (${this.bmapsInteraction ? 'with BMaps': 'no BMaps'})`;
    }
}
